import React from 'react'
import {WidgetProps} from '@wix/yoshi-flow-editor'
import {Text, ThreeDotsLoader} from 'wix-ui-tpa/cssVars'
import {RichContent} from '@wix/ricos'

import {LAYOUT_BREAKPOINTS_START} from '../constants'
import {withExperienceDetailsStorageProvider} from '../storage'
import {Breadcrumbs} from '../../../components-shared/Breadcrumbs'
import {withLayoutSizeProvider} from '../../../components-shared/LayoutSizeProvider'
import {TimeSlotsList} from '../../../components-shared/TimeSlotsList'
import {TimeSlotsListExtended} from '../components/TimeSlotsListExtended'
import {TagsList} from '../components/TagsList'
import {Header} from '../components/Header'
import {Form} from '../components/Form'
import {RicosViewer} from '../components/RicosViewer'
import {RequestStatus} from '../../../utils/wrapRequest'
import {TimeSlotsPageType} from '../../../utils/getTimeSlotsPageType'
import {ReserveButton} from '../components/ReserveButton'

import {useHooks} from './useHooks'
import {classes, st} from './Widget.st.css'

const Widget: React.FC<WidgetProps> = () => {
  const {
    t,
    submitButtonContentWidth,
    layoutSize,
    breadcrumbsConfig,
    experience,
    isInitialDataInProgress,
    showReservationForm,
    reservationLocation,
    // Reservation form data
    partySizeOptions,
    partySize,
    selectedDate,
    selectedTimeSlot,
    dayTimeSlots,
    timeSlotsByDate,
    experiencePageType,
    showNoAvailableTimeSlotNotification,
    showSelectTimeSlotError,
    regionalSettings,
    richContent,
    handleReservationDataStatus,
    // Handlers
    handlePartySizeChange,
    handleDateChange,
    handleFilterDate,
    handleTimeSlotChange,
    handleSubmit,
    // Scroll functionality
    reservationSectionRef,
    scrollToReservationSection,
  } = useHooks()

  if (!experience) {
    return <Text>{t('uou-experiences.experience-details.not-found')}</Text>
  }

  return (
    <div className={st(classes.root, {[layoutSize]: true})}>
      <div className={st(classes.breadcrumbs)}>
        <Breadcrumbs items={breadcrumbsConfig} />
      </div>

      <Header
        experience={experience}
        onReserveClick={scrollToReservationSection}
        regionalSettings={regionalSettings}
        layoutSize={layoutSize}
        showReserveButton={showReservationForm}
        timeZone={reservationLocation?.location?.timeZone}
      />

      <div className={st(classes.content)}>
        {richContent && (
          <div className={st(classes.contentSection)}>
            <Text tagName="h3" className={st(classes.sectionTitle)}>
              {t('uou-experiences.experience-details.section.about-experience')}
            </Text>
            <RicosViewer content={richContent as unknown as RichContent} />
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className={st(classes.contentSection)} ref={reservationSectionRef}>
            <Text tagName="h3" className={st(classes.sectionTitle)}>
              {t('uou-experiences.experience-details.section.reservation')}
            </Text>

            {showReservationForm ? (
              <>
                <Form
                  layoutSize={layoutSize}
                  regionalSettings={regionalSettings}
                  partySize={{
                    value: partySize.toString(),
                    options: partySizeOptions,
                    onChange: handlePartySizeChange,
                  }}
                  date={{
                    value: selectedDate,
                    filterDate: handleFilterDate,
                    onChange: handleDateChange,
                  }}
                />

                {experiencePageType === TimeSlotsPageType.DEFAULT && (
                  <TimeSlotsList
                    layoutSize={layoutSize}
                    selectedDate={selectedDate}
                    partySize={partySize}
                    regionalSettings={regionalSettings}
                    items={dayTimeSlots}
                    selectedTimeSlot={selectedTimeSlot}
                    showSelectTimeSlotError={showSelectTimeSlotError}
                    showNoAvailableTimeSlotNotification={showNoAvailableTimeSlotNotification}
                    onSelectTimeSlot={handleTimeSlotChange}
                    renderTagsList={(props) => <TagsList {...props} />}
                    notificationClassName={classes.timeSlotNotificationBodyText}
                  />
                )}

                {experiencePageType === TimeSlotsPageType.EXTENDED && (
                  <TimeSlotsListExtended
                    layoutSize={layoutSize}
                    regionalSettings={regionalSettings}
                    items={timeSlotsByDate}
                    partySize={partySize}
                    selectedDate={selectedDate}
                    selectedTimeSlot={selectedTimeSlot}
                    showSelectTimeSlotError={showSelectTimeSlotError}
                    showNoAvailableTimeSlotNotification={showNoAvailableTimeSlotNotification}
                    onSelectTimeSlot={handleTimeSlotChange}
                    notificationClassName={classes.timeSlotNotificationBodyText}
                    renderTagsList={(tagsListProps) => <TagsList {...tagsListProps} />}
                  />
                )}
                <ReserveButton width={submitButtonContentWidth}>
                  {isInitialDataInProgress ||
                  handleReservationDataStatus === RequestStatus.LOADING ? (
                    <ThreeDotsLoader className={classes.loader} />
                  ) : (
                    t('uou-reservations.experience-details.reserve-now-cta')
                  )}
                </ReserveButton>
              </>
            ) : (
              <Text>
                {t('uou-experiences.experience-details.section.reservation.not-accepting')}
              </Text>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default withLayoutSizeProvider(
  withExperienceDetailsStorageProvider(Widget),
  LAYOUT_BREAKPOINTS_START,
)
